/**
 * Calculate onboarding progress based on visible sections only
 * @param {object} completionData - Current completion data from Redux
 * @param {object} leadPreMeetingConfiguration - Lead configuration with visible/required settings
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateOnboardingProgress = (completionData, leadPreMeetingConfiguration) => {
  if (!completionData || !leadPreMeetingConfiguration) {
    return 0;
  }

  // Define all possible sections with their weights
  const sectionWeights = {
    personalDetails: 1,
    assets: 1,
    income: 1,
    liabilities: 1,
    expenses: 1,
    protectionPolicies: 1,
    goalsAndObjectives: 1,
    questionnaires: 1, // Questionnaires are always visible but not counted in progress
  };

  // Get visible sections from configuration
  const visibleSections = Object.keys(sectionWeights).filter(sectionKey => {
    const isVisible = leadPreMeetingConfiguration[sectionKey]?.visible;
    // Default to visible if not specified, except questionnaires
    return sectionKey === 'questionnaires' ? false : (isVisible !== false);
  });

  // Calculate total possible points (only visible sections)
  const totalPossiblePoints = visibleSections.reduce((total, sectionKey) => {
    return total + sectionWeights[sectionKey];
  }, 0);

  if (totalPossiblePoints === 0) {
    return 0;
  }

  // Calculate completed points (only visible sections)
  const completedPoints = visibleSections.reduce((total, sectionKey) => {
    const completion = completionData[sectionKey];
    if (completion === 100) {
      return total + sectionWeights[sectionKey];
    }
    return total;
  }, 0);

  // Calculate percentage
  const progressPercentage = Math.round((completedPoints / totalPossiblePoints) * 100);
  
  return Math.min(100, Math.max(0, progressPercentage));
};

/**
 * Get section weight for progress calculation
 * @param {string} sectionKey - Section identifier
 * @returns {number} - Weight of the section
 */
export const getSectionWeight = (sectionKey) => {
  const weights = {
    personalDetails: 1,
    assets: 1,
    income: 1,
    liabilities: 1,
    expenses: 1,
    protectionPolicies: 1,
    goalsAndObjectives: 1,
    questionnaires: 0, // Questionnaires don't count towards progress
  };
  
  return weights[sectionKey] || 0;
};

/**
 * Check if a section is visible and should count towards progress
 * @param {string} sectionKey - Section identifier
 * @param {object} leadPreMeetingConfiguration - Lead configuration
 * @returns {boolean} - Whether section is visible and counts towards progress
 */
export const isSectionVisibleForProgress = (sectionKey, leadPreMeetingConfiguration) => {
  if (sectionKey === 'questionnaires') {
    return false; // Questionnaires are always visible but don't count towards progress
  }
  
  const isVisible = leadPreMeetingConfiguration?.[sectionKey]?.visible;
  // Default to visible if not specified
  return isVisible !== false;
};
